import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';

export const useUserClaimedProfiles = () => {
  const [user, setUser] = useState(null);
  const [claimedProfiles, setClaimedProfiles] = useState({
    dancers: [],
    studios: []
  });
  const [loading, setLoading] = useState(true);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchClaimedProfiles = async () => {
      if (!user) {
        setClaimedProfiles({ dancers: [], studios: [] });
        setLoading(false);
        return;
      }

      try {
        const userEmail = user.email;
        console.log('Fetching profiles for user:', userEmail);
        
        // Fetch claimed dancer profiles
        const dancerProfiles = [];
        
        // Check dancers collection - handle both old string format and new object format
        const dancersQuery = query(
          collection(db, 'dancers'),
          where('claimedBy.email', '==', userEmail)
        );
        const dancersSnapshot = await getDocs(dancersQuery);
        
        dancersSnapshot.forEach(doc => {
          console.log('Found claimed dancer in dancers collection:', doc.id, doc.data());
          dancerProfiles.push({
            id: doc.id,
            name: doc.id,
            ...doc.data()
          });
        });

        // Also check for old string format in dancers collection
        const dancersQueryOld = query(
          collection(db, 'dancers'),
          where('claimedBy', '==', userEmail)
        );
        const dancersSnapshotOld = await getDocs(dancersQueryOld);
        
        dancersSnapshotOld.forEach(doc => {
          // Avoid duplicates
          const exists = dancerProfiles.some(profile => profile.name === doc.id);
          if (!exists) {
            console.log('Found claimed dancer (old format) in dancers collection:', doc.id, doc.data());
            dancerProfiles.push({
              id: doc.id,
              name: doc.id,
              ...doc.data()
            });
          }
        });
        
        // Check dancerProfiles collection - handle both object and string format
        const dancerProfilesQuery = query(
          collection(db, 'dancerProfiles'),
          where('claimedBy.email', '==', userEmail)
        );
        const dancerProfilesSnapshot = await getDocs(dancerProfilesQuery);
        
        dancerProfilesSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.profileClaimed) {
            // Avoid duplicates
            const exists = dancerProfiles.some(profile => profile.name === doc.id);
            if (!exists) {
              console.log('Found claimed dancer in dancerProfiles collection:', doc.id, data);
              dancerProfiles.push({
                id: doc.id,
                name: doc.id,
                ...data
              });
            }
          }
        });

        // Also check for old string format in dancerProfiles collection
        const dancerProfilesQueryOld = query(
          collection(db, 'dancerProfiles'),
          where('claimedBy', '==', userEmail)
        );
        const dancerProfilesSnapshotOld = await getDocs(dancerProfilesQueryOld);
        
        dancerProfilesSnapshotOld.forEach(doc => {
          const data = doc.data();
          if (data.profileClaimed) {
            // Avoid duplicates
            const exists = dancerProfiles.some(profile => profile.name === doc.id);
            if (!exists) {
              console.log('Found claimed dancer (old format) in dancerProfiles collection:', doc.id, data);
              dancerProfiles.push({
                id: doc.id,
                name: doc.id,
                ...data
              });
            }
          }
        });

        // Fetch claimed studio profiles - handle both object and string format
        const studioProfilesQuery = query(
          collection(db, 'studioProfiles'),
          where('claimedBy.email', '==', userEmail)
        );
        const studioProfilesSnapshot = await getDocs(studioProfilesQuery);
        
        const studioProfiles = [];
        studioProfilesSnapshot.forEach(doc => {
          const data = doc.data();
          if (data.profileClaimed) {
            console.log('Found claimed studio:', doc.id, data);
            studioProfiles.push({
              id: doc.id,
              name: doc.id,
              displayName: data.displayName || doc.id,
              ...data
            });
          }
        });

        // Also check for old string format in studioProfiles collection
        const studioProfilesQueryOld = query(
          collection(db, 'studioProfiles'),
          where('claimedBy', '==', userEmail)
        );
        const studioProfilesSnapshotOld = await getDocs(studioProfilesQueryOld);
        
        studioProfilesSnapshotOld.forEach(doc => {
          const data = doc.data();
          if (data.profileClaimed) {
            // Avoid duplicates
            const exists = studioProfiles.some(profile => profile.name === doc.id);
            if (!exists) {
              console.log('Found claimed studio (old format):', doc.id, data);
              studioProfiles.push({
                id: doc.id,
                name: doc.id,
                displayName: data.displayName || doc.id,
                ...data
              });
            }
          }
        });

        console.log('Final claimed profiles:', { dancers: dancerProfiles, studios: studioProfiles });

        setClaimedProfiles({
          dancers: dancerProfiles,
          studios: studioProfiles
        });
        
      } catch (error) {
        console.error('Error fetching claimed profiles:', error);
        setClaimedProfiles({ dancers: [], studios: [] });
      }
      
      setLoading(false);
    };

    fetchClaimedProfiles();
  }, [user]);

  return {
    claimedProfiles,
    loading,
    hasDancerProfiles: claimedProfiles.dancers.length > 0,
    hasStudioProfiles: claimedProfiles.studios.length > 0,
    hasAnyProfiles: claimedProfiles.dancers.length > 0 || claimedProfiles.studios.length > 0
  };
};
